import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { TextArea } from '@progress/kendo-react-inputs';
import React from 'react';
import DirectoryPicker from '../../components/common/DirectoryPicker';
import { CheckmarkOutline, CloseOutline, CogOutline, EditOutline, RefreshOutline } from '../../icons/icons';
import { getDefaultSetting, SettingKeys, SETTINGS_DEFINITIONS, SyncType } from '../../services/settings-service';
import { useAppDispatch, useAppSelector } from '../hooks/store-hooks';
import { setSetting } from '../state/slices/settings-slice';

export function DevSettings() {
    return (
        <div style={{ height: '80vh', width: 'min(80vw, 1900px)' }}>
            <table className="cobalt-table cobalt-alternate w-100">
                <thead style={{ position: 'sticky', top: 0, zIndex: 2 }} className="bg-color">
                    <tr>
                        <th className="ps-2">Name</th>
                        <th>Default</th>
                        <th>Current value</th>
                        <th>Sync</th>
                    </tr>
                </thead>
                <tbody>
                    {Object.entries(SETTINGS_DEFINITIONS).map((x) => (
                        <tr key={x[1].key}>
                            <td className="ps-2">{x[1].key}</td>
                            <td className="font-mono">{JSON.stringify(x[1].default)}</td>
                            <td>
                                <SettingsEditCell setting={x[0] as any} />
                            </td>
                            <td>{x[1].sync === SyncType.cloud && <SvgIcon icon={CogOutline} />}</td>
                        </tr>
                    ))}
                    <tr className="w-100">
                        <DirectoryPicker indexedDbKey="map-image" title="Default folder" />
                    </tr>
                </tbody>
            </table>
        </div>
    );
}

function SettingsEditCell(props: { setting: SettingKeys }) {
    const value = useAppSelector((x) => x.settings[props.setting]);
    const [editValue, setEditValue] = React.useState<string>('');
    const [editing, setEditing] = React.useState(false);
    const dispatch = useAppDispatch();
    const handleSave = () => {
        try {
            const value = JSON.parse(editValue);
            dispatch(setSetting(props.setting, value));
            setEditing(false);
            setEditValue('');
        } catch {
            // Too bad!
        }
    };

    return (
        <div className="d-flex gap-2 justify-content-between align-items-start">
            {!editing && (
                <pre className="font-mono" style={{ maxWidth: '100%', lineBreak: 'anywhere' }}>
                    {JSON.stringify(value, null, 2)}
                </pre>
            )}
            {editing && <TextArea style={{ minWidth: 200 }} value={editValue} onChange={(e) => setEditValue(e.value as string)} />}

            {editing && (
                <div className="d-flex gap-2">
                    <Button
                        svgIcon={CheckmarkOutline}
                        className="button-small flex-shrink-0"
                        fillMode={'outline'}
                        onClick={handleSave}
                        themeColor={'success'}
                    />
                    <Button
                        svgIcon={CloseOutline}
                        className="button-small flex-shrink-0"
                        fillMode={'outline'}
                        onClick={() => setEditing(false)}
                        themeColor={'error'}
                    />
                </div>
            )}
            {!editing && (
                <div className="d-flex gap-2">
                    <Button
                        svgIcon={EditOutline}
                        className="button-small"
                        themeColor="tertiary"
                        fillMode={'outline'}
                        onClick={() => {
                            setEditing(true);
                            setEditValue(JSON.stringify(value, null, 2));
                        }}
                    />
                    <Button
                        svgIcon={RefreshOutline}
                        className="button-small"
                        fillMode={'outline'}
                        themeColor={'info'}
                        onClick={() => dispatch(setSetting(props.setting, getDefaultSetting(props.setting)))}
                    />
                </div>
            )}
        </div>
    );
}
